import type { CopilotTurn } from './workflowDraft'

export interface ConversationReconciliation {
  outcome: 'adopted' | 'merged' | 'declined'
  turns: CopilotTurn[]
  id: string | null
}

function sameTurn(a: CopilotTurn, b: CopilotTurn): boolean {
  if (a.role === 'assistant' && b.role === 'assistant' && a.jobId && b.jobId) {
    if (a.jobId === b.jobId) return true
  }
  return a.role === b.role && a.content.trim() === b.content.trim()
}

function alignedTail(stored: CopilotTurn[], local: CopilotTurn[]): CopilotTurn[] {
  // offset === stored.length always matches because the overlap is empty.
  for (let offset = 0; offset <= stored.length; offset += 1) {
    const overlap = Math.min(stored.length - offset, local.length)
    if (local.slice(0, overlap).every((turn, index) => sameTurn(stored[offset + index], turn))) {
      return [...stored, ...local.slice(overlap)]
    }
  }
  throw new Error('unreachable')
}

export function reconcileConversation(args: {
  local: CopilotTurn[]
  localId: string | null
  localOwned: boolean
  stored: CopilotTurn[]
  storedId: string | null
}): ConversationReconciliation {
  const { local, localId, localOwned, stored, storedId } = args
  if (!localOwned) return { outcome: 'adopted', turns: stored, id: storedId }

  const sameConversation = (localId !== null && localId === storedId)
    || (localId === null && local.some(turn => stored.some(saved => sameTurn(turn, saved))))
  if (!sameConversation) return { outcome: 'declined', turns: local, id: localId }

  return {
    outcome: 'merged',
    turns: alignedTail(stored, local),
    id: storedId,
  }
}
